import React from 'react';
import {useIntl, IntlShape} from 'react-intl';
import Util from '../../../../forecast-app/shared/util/util';
import {buildEstimate, formatEstimateHours, ratePerUnit, type EstimateModel, type EstimateRow} from '../estimate';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';
import {themeTokens} from '../theme/themeTokens';
import * as S from './quotePreviewStyles';

const EM_DASH = '—';

const FIRST_COLUMN_HEADER_KEY: Record<EstimateModel['firstColumn'], string> = {
	roles: 'quote_designer.pdf.column.role',
	phase: 'common.phase',
	deliverable: 'common.deliverable',
	project: 'common.description',
};

type Formatters = {
	money: (amount: number) => string;
	time: (hours: number) => string;
	rate: (ratePerHour: number) => string;
};

const renderRow = (
	row: EstimateRow,
	index: number,
	model: EstimateModel,
	brand: string,
	intl: IntlShape,
	format: Formatters
): JSX.Element | null => {
	const numericCells = (hours: number | null, rate: number | null, total: number | null, boldTotal: boolean) => (
		<>
			{model.showTime && (
				<S.ColNum>
					<S.ItemNum>{hours == null ? EM_DASH : format.time(hours)}</S.ItemNum>
				</S.ColNum>
			)}
			{model.showRate && (
				<S.ColNum>
					<S.ItemNum>{rate == null ? EM_DASH : format.rate(rate)}</S.ItemNum>
				</S.ColNum>
			)}
			{model.showAmount && (
				<S.ColNum>
					{total == null ? null : boldTotal ? <S.ItemTotal>{format.money(total)}</S.ItemTotal> : format.money(total)}
				</S.ColNum>
			)}
		</>
	);

	switch (row.kind) {
		case 'phaseHeader':
			return (
				<S.PhaseRow key={index}>
					<S.ColLabel>
						<S.PhaseName $brand={brand}>{row.label}</S.PhaseName>
					</S.ColLabel>
				</S.PhaseRow>
			);
		case 'deliverableHeader':
			return (
				<S.DeliverableRow key={index}>
					<S.DeliverableLabel $brand={brand}>
						{intl.formatMessage({id: 'quote_designer.pdf.deliverable'})}
					</S.DeliverableLabel>
					<S.DeliverableName>{row.label}</S.DeliverableName>
				</S.DeliverableRow>
			);
		case 'item':
			return (
				<S.ItemRow key={index}>
					<S.ColLabel>
						{row.itemKind === 'expense' && (
							<S.ExpenseTag>{intl.formatMessage({id: 'quote_designer.pdf.expense'})}</S.ExpenseTag>
						)}
						<S.ItemName>{row.label}</S.ItemName>
					</S.ColLabel>
					{numericCells(row.hours, row.rate, row.total, true)}
				</S.ItemRow>
			);
		case 'phaseSubtotal':
			return (
				<S.PhaseSubtotalRow key={index}>
					<S.ColLabel>
						<S.ExpensesTotalLabel>
							{intl.formatMessage({id: 'quote_designer.pdf.phase_subtotal'}, {phase: row.label})}
						</S.ExpensesTotalLabel>
					</S.ColLabel>
					{model.showTime && <S.ColNum>{format.time(row.hours)}</S.ColNum>}
					{model.showRate && <S.ColNum />}
					{model.showAmount && (
						<S.ColNum>
							<S.ItemTotal>{format.money(row.total)}</S.ItemTotal>
						</S.ColNum>
					)}
				</S.PhaseSubtotalRow>
			);
		case 'expensesTotal':
			return (
				<S.ItemRow key={index}>
					<S.ColLabel>
						<S.ExpensesTotalLabel>
							{intl.formatMessage({id: 'quote_designer.pdf.expenses_row'})}
						</S.ExpensesTotalLabel>
					</S.ColLabel>
					{model.showTime && <S.ColNum />}
					{model.showRate && <S.ColNum />}
					{model.showAmount && (
						<S.ColNum>
							<S.ItemTotal>{format.money(row.total)}</S.ItemTotal>
						</S.ColNum>
					)}
				</S.ItemRow>
			);
		case 'rollup':
			return (
				<S.RollupRow key={index}>
					<S.ColLabel>
						<S.RollupName>{row.label}</S.RollupName>
					</S.ColLabel>
					{numericCells(row.hours, null, row.total, true)}
				</S.RollupRow>
			);
		default:
			return null;
	}
};

/**
 * The estimate section of the live preview — same model, formatting, and row set as the PDF's
 * estimate page (both consume buildEstimate), rendered as instant-updating HTML.
 */
const QuotePreviewEstimate: React.FC<{data: QuoteDocumentData; template: QuoteTemplate}> = ({data, template}) => {
	const intl = useIntl();
	const brand = template.branding.accentColor;
	const tokens = themeTokens(template.layout.theme, brand, template.branding.font);
	const model = buildEstimate(data, template);
	const currencySymbol = Util.GetCurrencySymbol(data.currency);
	const format: Formatters = {
		money: amount => Util.getFormattedNumberWithCurrency(currencySymbol, amount, intl),
		time: hours => formatEstimateHours(hours, model.unit, model.minutesPerDay),
		rate: rate =>
			Util.getFormattedNumberWithCurrency(currencySymbol, ratePerUnit(rate, model.unit, model.minutesPerDay), intl),
	};
	const days = model.unit === 'days';
	const {totals} = model;

	return (
		<S.Section data-cy="quote-preview-estimate">
			<S.SectionLabelRow $tokens={tokens}>
				<S.SectionLabelText $brand={brand} $tokens={tokens}>
					{intl.formatMessage({id: 'quote_designer.section.estimate'})}
				</S.SectionLabelText>
			</S.SectionLabelRow>

			<S.TableHeadRow $tokens={tokens}>
				<S.ColLabel>
					<S.Th $tokens={tokens}>{intl.formatMessage({id: FIRST_COLUMN_HEADER_KEY[model.firstColumn]})}</S.Th>
				</S.ColLabel>
				{model.showTime && (
					<S.ColNum>
						<S.Th $num $tokens={tokens}>
							{intl.formatMessage({id: days ? 'common.days' : 'common.hours'})}
						</S.Th>
					</S.ColNum>
				)}
				{model.showRate && (
					<S.ColNum>
						<S.Th $num $tokens={tokens}>
							{intl.formatMessage({
								id: days ? 'quote_designer.pdf.column.rate_day' : 'quote_designer.pdf.column.rate_hour',
							})}
						</S.Th>
					</S.ColNum>
				)}
				{model.showAmount && (
					<S.ColNum>
						<S.Th $num $tokens={tokens}>
							{intl.formatMessage({id: 'quote_designer.pdf.column.amount'})}
						</S.Th>
					</S.ColNum>
				)}
			</S.TableHeadRow>

			{model.rows.map((row, index) => renderRow(row, index, model, brand, intl, format))}

			<S.TotalsWrap>
				<S.TotalsGrid>
					{template.estimate.totals.subtotal && (
						<S.TotalsRow>
							<S.TotalsLabel>{intl.formatMessage({id: 'common.subtotal'})}</S.TotalsLabel>
							<span>{format.money(totals.subtotal)}</span>
						</S.TotalsRow>
					)}
					{template.estimate.totals.expenses && (
						<S.TotalsRow>
							<S.TotalsLabel>{intl.formatMessage({id: 'quote_designer.pdf.totals.expenses'})}</S.TotalsLabel>
							<span>{format.money(totals.expenses)}</span>
						</S.TotalsRow>
					)}
					{template.estimate.totals.grandTotal && (
						<S.TotalsGrandRow $tokens={tokens}>
							<span>{intl.formatMessage({id: 'quote_designer.pdf.totals.grand'})}</span>
							<S.TotalsGrandValue $brand={brand}>{format.money(totals.grandTotal)}</S.TotalsGrandValue>
						</S.TotalsGrandRow>
					)}
				</S.TotalsGrid>
			</S.TotalsWrap>
		</S.Section>
	);
};

export default QuotePreviewEstimate;
